import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Calendar, Clock, MapPin, ArrowRight, Tag, Search } from "lucide-react";
import { api } from "../lib/api";

const CATEGORIES = ["Tous", "EMS", "Massages", "Sport", "Bien-être", "Crédit d'impôt"];

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [filter, setFilter] = useState("Tous");
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get("/blog").then((r) => setPosts(r.data)).finally(() => setLoading(false));
  }, []);

  const filtered = posts.filter((p) => {
    const matchCat = filter === "Tous" || p.category === filter;
    const q = query.trim().toLowerCase();
    if (!q) return matchCat;
    return matchCat && (
      p.title.toLowerCase().includes(q) ||
      p.excerpt.toLowerCase().includes(q) ||
      (p.keywords || []).some((k) => k.toLowerCase().includes(q)) ||
      (p.locality || "").toLowerCase().includes(q)
    );
  });

  return (
    <div className="bg-white">
      {/* Hero */}
      <section className="bg-fegray-light pt-12 lg:pt-16 pb-12 lg:pb-16 px-6 lg:px-10" data-testid="blog-hero">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-fegreen font-medium uppercase tracking-[0.25em] text-xs mb-4">
            — Blog Form&apos;elec
          </p>
          <h1 className="text-3xl sm:text-4xl lg:text-6xl font-bold text-fegray-dark">
            Conseils <span className="text-fegreen">EMS &amp; bien-être</span>
          </h1>
          <p className="mt-6 text-fegray max-w-2xl mx-auto">
            Toute l&apos;actualité de l&apos;électrostimulation, des massages
            et du sport à domicile à Istres, Fos-sur-Mer, Miramas &amp;
            Bouches-du-Rhône.
          </p>
        </div>
      </section>

      {/* Filtres */}
      <section className="py-8 px-6 lg:px-10 border-b border-gray-100">
        <div className="max-w-7xl mx-auto flex flex-col lg:flex-row lg:items-center gap-4 justify-between">
          <div className="flex flex-wrap gap-2">
            {CATEGORIES.map((c) => (
              <button
                key={c}
                onClick={() => setFilter(c)}
                data-testid={`blog-filter-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className={`px-4 py-2 rounded-full text-xs sm:text-sm font-semibold transition-all ${
                  filter === c
                    ? "bg-fegreen text-white shadow-md"
                    : "bg-fegray-light text-fegray hover:bg-fegreen-light hover:text-fegreen"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
          <div className="relative w-full lg:w-72">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-fegray/50" />
            <input
              type="search"
              placeholder="Rechercher un article…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              data-testid="blog-search"
              className="w-full bg-fegray-light border-0 rounded-full pl-10 pr-4 py-2.5 text-sm text-fegray-dark focus:bg-white focus:ring-2 focus:ring-fegreen/30 focus:outline-none"
            />
          </div>
        </div>
      </section>

      {/* Posts grid */}
      <section className="py-12 lg:py-16 px-6 lg:px-10">
        <div className="max-w-7xl mx-auto">
          {loading ? (
            <div className="text-center text-fegray py-20">Chargement…</div>
          ) : filtered.length === 0 ? (
            <div className="text-center text-fegray py-20">Aucun article trouvé.</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
              {filtered.map((p) => (
                <Link
                  key={p.slug}
                  to={`/blog/${p.slug}`}
                  className="fe-card overflow-hidden group"
                  data-testid={`blog-card-${p.slug}`}
                >
                  <div className="aspect-[16/10] overflow-hidden bg-fegray-light">
                    <img
                      src={p.cover}
                      alt={p.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-3 mb-3 text-xs">
                      <span className="px-2.5 py-1 rounded-full bg-fegreen-light text-fegreen font-bold uppercase tracking-wider">
                        {p.category}
                      </span>
                      {p.locality && (
                        <span className="flex items-center gap-1 text-fegray">
                          <MapPin className="w-3 h-3" /> {p.locality}
                        </span>
                      )}
                    </div>
                    <h2 className="text-lg font-bold text-fegray-dark mb-2 group-hover:text-fegreen transition-colors line-clamp-2">
                      {p.title}
                    </h2>
                    <p className="text-sm text-fegray line-clamp-3 mb-4">{p.excerpt}</p>
                    <div className="flex items-center justify-between text-xs text-fegray pt-3 border-t border-gray-100">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {new Date(p.published_at).toLocaleDateString("fr-FR")}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" /> {p.read_time} min
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
